/* eslint-disable max-lines */
import { DropDownOptions, ReportColumns, ReportFilter } from 'models/report';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CommonButton from 'components/Buttons/CommonButton';
import { GetInfoForQuery } from 'models/response';
import Modal from 'components/Modal';
import ModalsContent from 'routes/Report/components/ModalsContent';
import SelectFilters from '../components/SelectFilters';
import SelectReport from '../components/SelectReport';
import SelectTemplate from '../components/SelectTemplate';
import ThreeColumnsLayout from 'components/Layout/ThreeColumnsLayout';
import { Title } from '../styles';
import action from 'state/actions';
import { modalActionType } from 'utils/constants';
import selectors from 'state/selectors';
import services from 'services';
import styled from 'styled-components/macro';
import { useAsync } from 'react-use';
import utils from 'utils';
import SelectFilterPreference from '../components/SelectFilterPreference';
import { OptionsFiltersPrefererence } from 'models/userReportSettings';

const ExportReportWrapper = styled.div`
  align-self: flex-end;
  justify-content: space-between;
`;
const WrapperHeader = styled(Title)`
  justify-content: space-between;
  padding: 20px;
`;
const WrapperLeftCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

export default () => {
  const [selectedFolder, setSelectedFolder] = useState<DropDownOptions>({ label: '', value: '0' });
  const [selectedReport, setSelectedReport] = useState<DropDownOptions>({ label: '', value: '0' });
  const [folderOptions, setFolderOptions] = useState<DropDownOptions[]>([]);
  const [allReportOptions, setAllReportOptions] = useState<any[]>([]);
  // options filtered by current folder
  const [reportOptions, setReportOptions] = useState<DropDownOptions[] | any[]>([]);
  const [isReportLoaded, setIsReportLoaded] = useState(false);
  const idUser = useSelector(selectors.user.getIdUser);
  const idProgram = useSelector(selectors.app.getProgramCode);
  const isModalVisible = useSelector(selectors.modal.getVisibility);
  const [responseDetails, setResponseDetails] = useState<GetInfoForQuery | null>(null);
  const [columns, setColumns] = useState<Array<ReportColumns>>([]);
  const [selectedManualFilters, setSelectedManualFilters] = useState<ReportFilter[]>([]);
  const [selectFiltersKey, setSelectFiltersKey] = useState<number>(0);
  const [selectTemplateKey, setSelectTemplateKey] = useState<number>(0);
  const [errorObject, setErrorObject] = useState({});
  const [counterRows, setCounterRows] = useState<number | undefined>(undefined);

  const optionsFilterPreference = useSelector(selectors.userReportSettings.getOptionsFilterPreference);
  const selectedFilterPreference = useSelector(selectors.userReportSettings.getSelectedFilterPreference);
  const filterModified = selectedManualFilters.map((filter) => {
    if (filter.operationType?.[0] !== 'IN') {
      return filter;
    }
    const valueFilter = filter.listOfValues?.length ? filter.listOfValues[0].code : '';
    if (!valueFilter) {
      return filter;
    }
    const newListOfValues = valueFilter.split('\n').map((value) => ({ code: value, name: null }));
    return {
      ...filter,
      listOfValues: newListOfValues,
    };
  });
  const t = utils.intl.useTranslator();
  const dispatch = useDispatch();

  useEffect(() => {
    if (responseDetails) {
      setIsReportLoaded(true);
      setColumns(
        responseDetails.selectItems.map(({ columnHeader, columnName, format, formatter }) => ({
          label: columnName,
          name: columnName || '',
          value: true,
          header: columnHeader,
          format: format,
          formatter: formatter,
        })),
      );
      setSelectedManualFilters(
        responseDetails.filters.map((filter: ReportFilter) => ({
          ...filter,
          listOfValues: [],
          operationType: filter.operationType && filter.operationType.length > 0 ? [filter.operationType[0]] : [],
        })),
      );
    }
  }, [responseDetails]);

  // api call on mount
  useAsync(async () => {
    if (idUser) {
      try {
        const { data } = await services.getReports(idUser);
        let folderData = data.reports.map((folder) => ({
          value: folder.idReport.toString(),
          label: folder.folderName,
        }));
        folderData = folderData.filter((el) => el.value !== '0');
        const fileData = data.reports.map(({ children }) =>
          children.map((child) => ({ idParent: child.idParent, value: child.idReport, label: child.reportName })),
        );
        setFolderOptions(folderData);
        setSelectedFolder(folderData[0] || null);
        setAllReportOptions(fileData);
      } catch (e) {
        console.error(e);
      }
    }
  }, []);

  useAsync(async () => {
    const result = await services.getInfoForQuery(Number(selectedReport.value), idUser, idProgram);
    setResponseDetails(result.data);
    setErrorObject({});
  }, [selectedReport]);

  // update file options based on folder
  useEffect(() => {
    // keep only options of the current folder
    const optionsBasedOnFolder = allReportOptions.filter(
      (el) => el.length && el[0].idParent.toString() === selectedFolder.value,
    );
    // clean and format data for dropdown
    if (optionsBasedOnFolder.length > 0) {
      let cleanedOptions = Object.entries(optionsBasedOnFolder[0]).map((el) => el[1]);
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      cleanedOptions = cleanedOptions.map(({ idParent, ...rest }) => rest);
      setReportOptions(cleanedOptions);
    } else {
      setReportOptions([]);
    }
    setSelectedReport({ label: '', value: '0' });
  }, [selectedFolder, allReportOptions]);

  const handleChangeFolder = (option: DropDownOptions) => {
    setSelectedFolder(option);
    setCounterRows(undefined);
  };

  const handleChangeReport = async (option: DropDownOptions) => {
    setSelectedReport(option);
    setSelectFiltersKey((prev) => prev + 1);
    setSelectTemplateKey((prev) => prev + 1);
    setCounterRows(undefined);

    const { data } = await services.loadFiltersPreference(parseInt(selectedFolder.value), parseInt(option.value));
    dispatch(action.userReportSettings.setOptionsFilterPreference(data));
    dispatch(action.userReportSettings.setSelectedFilterPreference(null));
  };

  const selectOptionFilterPreference = (option: { value: number; label: string } | null) => {
    if (!option) {
      dispatch(action.userReportSettings.setSelectedFilterPreference(null));
      return;
    }

    const { value } = option;
    const { idUserReportSetting } = selectedFilterPreference ?? {};

    if (idUserReportSetting !== undefined && value === idUserReportSetting) return;

    const selectedOption = optionsFilterPreference.find((el) => el.idUserReportSetting === value);
    if (selectedOption) {
      dispatch(action.userReportSettings.setSelectedFilterPreference(selectedOption));
    }
  };

  const handleRemoveFilters = () => {
    setErrorObject({});
    setSelectFiltersKey((prev) => prev + 1);
    responseDetails &&
      setSelectedManualFilters(
        responseDetails.filters.map((filter: ReportFilter) => ({
          ...filter,
          listOfValues: [],
          operationType: filter.operationType && filter.operationType.length > 0 ? [filter.operationType[0]] : [],
        })),
      );
  };

  const leftCol = [
    <WrapperLeftCol key={'wrapper-left-col'}>
      <SelectReport
        key={'select-report'}
        onChangeFolder={handleChangeFolder}
        onChangeReport={handleChangeReport}
        selectedFolder={selectedFolder}
        selectedReport={selectedReport}
        optionsFolder={folderOptions}
        optionsReport={reportOptions}
      />
      {selectedReport.value !== '0' ? (
        <SelectFilterPreference
          key={'select-filter-preference'}
          options={optionsFilterPreference}
          setOptions={(value: { value: number; label: string } | null) => {
            handleRemoveFilters();
            selectOptionFilterPreference(value);
          }}
        />
      ) : null}
    </WrapperLeftCol>,
  ];

  const onSelectColumn = (checked: boolean, item: any) => {
    const newColumns = [...columns];
    const newItem = newColumns.find(({ name }) => name === item.name);
    if (newItem) {
      newItem.value = checked;
    }

    setColumns(newColumns);
  };

  const centerCol = [
    <SelectTemplate
      selectItems={columns}
      changeColumns={onSelectColumn}
      key={`select-template-${selectTemplateKey}`}
      visible={selectedFolder.value !== '0' && selectedReport.value !== '0' && isReportLoaded}
    />,
  ];

  const closeModal = () => dispatch(action.modal.closeModal());

  const checkValidateFilters = () => {
    const requiredMissing = selectedManualFilters.filter((item) => {
      if (item.operationType && item.operationType[0] === 'BETWEEN') {
        if (item.mandatory) {
          return item.listOfValues && item.listOfValues.length < 2;
        }
        // if item not mandatory, we cant send just 1 value, 0 or 2 values
        return item.listOfValues && item.listOfValues.length === 1;
      }
      return item.listOfValues && item.mandatory && item.listOfValues.length === 0;
    });

    const obj = requiredMissing.reduce(
      (prev, current) => ({
        ...prev,
        [current.column]: t('fieldrequired'),
      }),
      {},
    );
    setErrorObject(obj);
    return Object.keys(obj).length === 0;
  };

  const openExportModal = () => {
    const documentsCheckResult = checkValidateFilters();
    if (documentsCheckResult) {
      const data = {
        payload: {
          idReport: selectedReport.value,
          idUser: idUser,
          queryReportInfo: {
            source: responseDetails?.source,
            table: responseDetails?.table,
            selectItems: columns
              .filter(({ value }) => value === true)
              .map(({ header, name, format, formatter }) => ({
                columnHeader: header,
                columnName: name,
                format,
                formatter,
              })),
            filters: filterModified,
          },
          outputFormat: 'CSV',
          async: false,
        },
        csvFormat: responseDetails?.csvFormat ?? false,
      };
      dispatch(action.modal.openModal(modalActionType.report.EXPORT_REPORT, data));
    }
  };

  const calculateRowsFunction = async () => {
    const documentsCheckResult = checkValidateFilters();

    if (documentsCheckResult) {
      const filters = filterModified.filter((item) => item.listOfValues && item.listOfValues.length > 0);

      if (responseDetails) {
        const { data } = await services.calculateRows({
          source: responseDetails?.source,
          table: responseDetails?.table,
          selectItems: columns
            .filter(({ value }) => value === true)
            .map(({ header, name }) => ({ columnHeader: header, columnName: name })),
          filters: filters as ReportFilter[],
        });
        setCounterRows(data);
      }
    }
  };

  const saveFilters = () => {
    const filters = selectedManualFilters.filter((item) => item.listOfValues && item.listOfValues.length > 0);
    dispatch(
      action.modal.openModal(modalActionType.report.SAVE_OR_UPDATE_FILTERS, {
        selectedFolder: selectedFolder,
        selectedReport: selectedReport,
        selectItems: columns
          .filter(({ value }) => value === true)
          .map(({ header, name }) => ({ columnHeader: header, columnName: name })),
        filters: filters,
      }),
    );
  };

  const prevSelectedFilterPreference = useRef<OptionsFiltersPrefererence | null>(null);

  useEffect(() => {
    if (!prevSelectedFilterPreference.current || prevSelectedFilterPreference.current !== selectedFilterPreference) {
      if (selectedFilterPreference) {
        const updatedColumns = columns.map((column) => ({
          ...column,
          value: selectedFilterPreference.selectItemsJson.some(
            (item) => item.columnName === column.name && item.columnHeader === column.header,
          ),
        }));
        setColumns(updatedColumns);

        const updatedSelectedManualFilters = selectedManualFilters.map((filter) => {
          const matchingFilter = selectedFilterPreference.filtersJson.find((f) => f.column === filter.column);
          if (matchingFilter) {
            return {
              ...matchingFilter,
            };
          } else {
            return filter;
          }
        });
        setSelectedManualFilters(updatedSelectedManualFilters);
      }

      prevSelectedFilterPreference.current = selectedFilterPreference;
    }
  }, [selectedFilterPreference, columns, selectedManualFilters]);

  const rightCol = [
    <SelectFilters
      selectedManualFilters={selectedManualFilters}
      setSelectedManualFilters={setSelectedManualFilters}
      removeFilters={handleRemoveFilters}
      filters={responseDetails?.filters || []}
      key={`select-filters-${selectFiltersKey}`}
      visible={selectedFolder.value !== '0' && selectedReport.value !== '0' && isReportLoaded}
      requiredMissingValues={errorObject}
      setRequiredMissinValues={setErrorObject}
      calculateRowsFunction={calculateRowsFunction}
      totalRows={counterRows}
      saveFilters={saveFilters}
    />,
  ];

  return (
    <>
      <WrapperHeader>
        <h2>{t('report-options')}</h2>
        <ExportReportWrapper>
          <CommonButton
            id="exportButton"
            scope="primary"
            disabled={selectedFolder.value === '0' || selectedReport.value === '0' || !isReportLoaded}
            type="button"
            action={openExportModal}
            value={t('report-export-report')}
          />
        </ExportReportWrapper>
      </WrapperHeader>
      <ThreeColumnsLayout
        leftWidth={28}
        centerWidth={22}
        rightWidth={50}
        leftColumn={leftCol}
        centerColumn={centerCol}
        rightColumn={rightCol}
      />
      <Modal onClose={closeModal} open={isModalVisible} isDraggable={true}>
        <ModalsContent />
      </Modal>
    </>
  );
};
